import type { AioShopperInfo, IShopper } from '@makemydeal/dr-platform-types';

import { getCustomerCardBffBaseUrl, getCustomerCardConfig } from '../config';
import { logCustomerCardError, logCustomerCardPageAction } from '../utils/newRelicLogging';
import type { CustomerCardStoreState } from '../types/CustomerCardTypes';
import { saveParticipantsRSAA } from './actionCreators';
import { saveFailureFacets } from './saveFailureFacets';
import { DROP_CO_BUYER_FAILURE, DROP_CO_BUYER_REQUEST, DROP_CO_BUYER_RESET, DROP_CO_BUYER_SUCCESS } from './actions';
import * as customerCardSelectors from './selectors';

export type DropCoBuyerResult = { ok: true } | { ok: false; error: string };

export const resetDropCoBuyerStatus = () => ({ type: DROP_CO_BUYER_RESET });

type DropThunkDispatch = (action: any) => any;
type DropThunkGetState = () => CustomerCardStoreState;

const DROP_CO_BUYER_FAILED = 'DropCoBuyerFailed';
const DROP_CO_BUYER_OPERATION = 'dropCoBuyer';

const dropEventName = (outcome: string): string => {
    const configuredHost = getCustomerCardConfig()?.hostApp;
    return configuredHost ? `${configuredHost}:DropCoBuyer${outcome}` : `DropCoBuyer${outcome}`;
};

export const dropCoBuyer =
    (buyer: IShopper) =>
    async (dispatch: DropThunkDispatch, getState: DropThunkGetState): Promise<DropCoBuyerResult> => {
        const state = getState();
        const dealId = customerCardSelectors.getDealId(state);

        const failed = (error: string, facets: Record<string, unknown> = {}): DropCoBuyerResult => {
            dispatch({ type: DROP_CO_BUYER_FAILURE, payload: { error } });
            logCustomerCardPageAction(dropEventName('Failed'), {
                operation: DROP_CO_BUYER_OPERATION,
                error,
                dealId,
                ...facets
            });
            return { ok: false, error };
        };

        if (customerCardSelectors.getIsSaving(state)) {
            logCustomerCardError(
                'DropCoBuyerBlockedByCustomerCardSave',
                `A customer card save was already in flight for deal ${dealId}`
            );
            return failed('CustomerCardSaveInFlight');
        }

        const bffBaseUrl = getCustomerCardBffBaseUrl();
        if (!bffBaseUrl || !dealId) {
            return failed('MissingConfiguration');
        }

        try {
            dispatch({ type: DROP_CO_BUYER_REQUEST });
            logCustomerCardPageAction(dropEventName('Started'), { operation: DROP_CO_BUYER_OPERATION, dealId });

            const dealerUser = customerCardSelectors.getBridgeUsername(state);
            const dealerId = String(customerCardSelectors.getDealerId(state) ?? '');
            const payload = { buyer: buyer as Partial<AioShopperInfo>, ...(dealerUser && { dealerUser }) };

            await dispatch(saveParticipantsRSAA(bffBaseUrl, dealId, dealerId, payload));

            const recordedError = customerCardSelectors.getSaveParticipantsError(getState());
            if (recordedError) {
                return failed(DROP_CO_BUYER_FAILED, saveFailureFacets(recordedError));
            }

            dispatch({ type: DROP_CO_BUYER_SUCCESS });
            logCustomerCardPageAction(dropEventName('Completed'), { operation: DROP_CO_BUYER_OPERATION, dealId });

            return { ok: true };
        } catch (error) {
            logCustomerCardError(DROP_CO_BUYER_FAILED, `Drop co-buyer threw for deal ${dealId}: ${error}`);
            return failed(DROP_CO_BUYER_FAILED);
        }
    };
